import { useEffect, useRef, useState } from "react";
import { Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "./NotificationBell.css";

const API = import.meta.env.VITE_API_URL ?? "http://localhost:5050";

interface RoomRef { roomId: string; roomNumber: string; }

interface BookingItem {
  id: string;
  bookingId: string;
  guestName: string;
  roomNumber: string;
  roomNumbers: string;
  allRooms: RoomRef[];
  checkIn: string | null;
  checkOut: string | null;
  nights: number;
  total: number;
  type: "arrival" | "departure" | "upcoming" | "booking";
}

interface ActivityItem {
  id: number | string;
  type: "activity";
  activityType: string;
  guestname: string;
  roomnumber: string;
  bookingid?: string;
  message: string;
  is_read: boolean;
  created_at: string;
}

type NotifItem = BookingItem | ActivityItem;

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const BOOKING_META = {
  arrival:   { label: "Check-in",  cls: "nb-tag-green",  dot: "#34d399" },
  departure: { label: "Check-out", cls: "nb-tag-blue",   dot: "#60a5fa" },
  upcoming:  { label: "Upcoming",  cls: "nb-tag-indigo", dot: "#C9A84C" },
  booking:   { label: "New",       cls: "nb-tag-gold",   dot: "#fbbf24" },
};

const ACTIVITY_META: Record<string, { label: string; cls: string; dot: string }> = {
  checkin:            { label: "Checked In",     cls: "nb-tag-green",   dot: "#34d399" },
  check_in:           { label: "Checked In",     cls: "nb-tag-green",   dot: "#34d399" },
  checkout:           { label: "Checked Out",    cls: "nb-tag-blue",    dot: "#60a5fa" },
  check_out:          { label: "Checked Out",    cls: "nb-tag-blue",    dot: "#60a5fa" },
  new_booking:        { label: "New Booking",    cls: "nb-tag-gold",    dot: "#fbbf24" },
  pre_checkin:        { label: "Pre Check-in",   cls: "nb-tag-indigo",  dot: "#C9A84C" },
  housekeeping:       { label: "Housekeeping",   cls: "nb-tag-orange",  dot: "#f97316" },
  whatsapp_booking:   { label: "WA Booking",     cls: "nb-tag-green",   dot: "#25D366" },
  whatsapp_handoff:   { label: "Handoff Alert",  cls: "nb-tag-red",     dot: "#f87171" },
  whatsapp_off_hours: { label: "After Hours",    cls: "nb-tag-indigo",  dot: "#a78bfa" },
  whatsapp_alert:     { label: "New Message",    cls: "nb-tag-green",   dot: "#25D366" },
  cancellation:       { label: "Cancelled",      cls: "nb-tag-red",     dot: "#f87171" },
  payment:            { label: "Payment",        cls: "nb-tag-purple",  dot: "#a78bfa" },
  // ── n8n notification types (BOOKING.JSON, friendly.json, messages.json, checkin.json) ──
  booking_lead:       { label: "Booking Lead",   cls: "nb-tag-gold",    dot: "#fbbf24" },
  off_hours:          { label: "After Hours",    cls: "nb-tag-indigo",  dot: "#a78bfa" },
  handoff:            { label: "Handoff Alert",  cls: "nb-tag-red",     dot: "#f87171" },
  // ── Room service / restaurant orders (derived from the orders table) ──
  room_service_order: { label: "Room Service",   cls: "nb-tag-orange",  dot: "#f97316" },
  restaurant_order:   { label: "Restaurant",     cls: "nb-tag-orange",  dot: "#f97316" },
};

function playSound(urgent = false) {
  try {
    const ctx = new AudioContext();
    ctx.resume().then(() => {
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      if (urgent) {
        [0, 0.18, 0.36].forEach(offset => {
          const osc = ctx.createOscillator();
          osc.connect(gain);
          osc.type = "square";
          osc.frequency.setValueAtTime(880, ctx.currentTime + offset);
          gain.gain.setValueAtTime(0.12, ctx.currentTime + offset);
          gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + offset + 0.14);
          osc.start(ctx.currentTime + offset);
          osc.stop(ctx.currentTime + offset + 0.15);
        });
      } else {
        const osc = ctx.createOscillator();
        osc.connect(gain);
        osc.type = "sine";
        osc.frequency.setValueAtTime(880, ctx.currentTime);
        osc.frequency.exponentialRampToValueAtTime(520, ctx.currentTime + 0.28);
        gain.gain.setValueAtTime(0.14, ctx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.32);
        osc.start(ctx.currentTime);
        osc.stop(ctx.currentTime + 0.35);
      }
    });
  } catch { }
}

let _titleFlashInterval: ReturnType<typeof setInterval> | null = null;
let _originalTitle = "";

function flashTitle(count: number, urgent: boolean) {
  if (_titleFlashInterval) clearInterval(_titleFlashInterval);
  _originalTitle = document.title.replace(/^\(\d+\) .*? \| /, "").replace(/^🔴 /, "");
  const alertText = urgent ? `🔴 HANDOFF ALERT` : `(${count}) New message`;
  let toggle = true;
  _titleFlashInterval = setInterval(() => {
    document.title = toggle ? `${alertText} | ${_originalTitle}` : _originalTitle;
    toggle = !toggle;
  }, 900);
  // Stop after 45s
  setTimeout(() => {
    if (_titleFlashInterval) { clearInterval(_titleFlashInterval); _titleFlashInterval = null; }
    document.title = _originalTitle;
  }, 45_000);
}

function showBrowserNotif(title: string, body: string, urgent: boolean, id: number | string) {
  // Title flash works universally — iOS Safari, Chrome mobile, everything
  flashTitle(1, urgent);

  // Browser push notification — desktop + Android Chrome (NOT iOS Safari regular tab)
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body,
      icon: "/Logo.png",
      tag: `notif-${id}`,
      requireInteraction: urgent,
      silent: true,
    });
    if (!urgent) setTimeout(() => n.close(), 6000);
  } catch { /* not supported */ }
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotifItem[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Key-based tracking — uses id if available, falls back to timestamp+type+name
  const seenKeysRef = useRef<Set<string>>(new Set());
  const isInitRef   = useRef(true);

  const makeKey = (a: ActivityItem) =>
    a.id != null ? `id:${a.id}` : `ts:${a.created_at}|${a.type}|${a.guestname}`;

  // Request browser notification permission once
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchNotifs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/dashboard/notifications`);
      const booking: BookingItem[] = [
        ...(data.todayArrivals || []).map((b: any) => ({ ...b, type: "arrival" as const })),
        ...(data.todayDepartures || []).map((b: any) => ({ ...b, type: "departure" as const })),
        ...(data.upcomingArrivals || []).slice(0, 3).map((b: any) => ({ ...b, type: "upcoming" as const })),
        ...(data.recentBookings || []).slice(0, 2).map((b: any) => ({ ...b, type: "booking" as const })),
      ];
      const activity: ActivityItem[] = (data.activityLog || []).map((a: any) => ({
        id: a.id,
        type: "activity" as const,
        activityType: a.type,
        guestname: a.guestname || "—",
        roomnumber: a.roomnumber || "",
        bookingid: a.bookingid || "",
        message: a.message || "",
        is_read: a.is_read,
        created_at: a.created_at,
      }));
      setItems([...activity, ...booking]);
      const unreadCount =
        (data.todayArrivals || []).length +
        (data.todayDepartures || []).length +
        activity.filter(a => !a.is_read).length;
      setUnread(unreadCount);
      return activity;
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
    return null;
  };

  useEffect(() => {
    const run = async (isInit: boolean) => {
      const activity = await fetchNotifs();
      if (!activity) return;

      // Find items we haven't seen yet — use composite key so null IDs don't collapse
      const fresh = activity.filter(a => !seenKeysRef.current.has(makeKey(a)));

      if (!isInit && fresh.length > 0) {
        const isHandoff = (t: string) => t === "handoff" || t === "whatsapp_handoff";
        const hasHandoff = fresh.some(a => isHandoff(a.activityType));

        // One sound per batch (urgent triple-beep if any handoff)
        playSound(hasHandoff);

        // One browser / title notification per item
        fresh.forEach(a => {
          const meta  = ACTIVITY_META[a.activityType];
          const title = meta?.label ?? "Hotel Notification";
          const body  = a.message
            ? a.message.slice(0, 120)
            : `${a.guestname}${a.roomnumber ? ` · Room ${a.roomnumber}` : ""}`;
          showBrowserNotif(title, body, isHandoff(a.activityType), a.id ?? 0);
        });
      }

      // Mark all as seen so they never fire again
      activity.forEach(a => seenKeysRef.current.add(makeKey(a)));
      isInitRef.current = false;
    };

    run(true); // first load — populate seenIds without triggering notifications
    const t = setInterval(() => run(false), 15_000);
    return () => clearInterval(t);
  }, []);

  const handleOpen = () => {
    const opening = !open;
    setOpen(opening);
    if (opening) {
      setUnread(0);
      axios.patch(`${API}/api/dashboard/notifications/mark-read`, {}).catch(() => {});
      setTimeout(fetchNotifs, 500);
    }
  };

  const goFull = () => {
    setOpen(false);
    navigate("/dashboard/manager/notifications");
  };

  const goBooking = (bookingId?: string) => {
    if (!bookingId) return;
    setOpen(false);
    navigate(`/dashboard/manager/bookings?id=${bookingId}`);
  };

  return (
    <div className="nb-wrap" ref={wrapRef}>
      <button className={`nb-bell-btn${open ? " active" : ""}`} onClick={handleOpen} aria-label="Notifications">
        <Bell size={22} />
        {unread > 0 && (
          <span className="nb-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {open && (
        <div className="nb-dropdown">
          <div className="nb-dropdown-header">
            <span className="nb-dropdown-title">Notifications</span>
            {unread > 0 && <span className="nb-unread-pill">{unread} new</span>}
          </div>

          <div className="nb-list">
            {loading && items.length === 0 && (
              <div className="nb-loading">Loading…</div>
            )}
            {!loading && items.length === 0 && (
              <div className="nb-empty">No notifications</div>
            )}
            {items.slice(0, 12).map((item, i) => {
              if (item.type === "activity") {
                const meta = ACTIVITY_META[item.activityType] || { label: item.activityType, cls: "nb-tag-gold", dot: "#fbbf24" };
                const hasBooking = !!item.bookingid;
                return (
                  <div
                    key={`act-${item.id}`}
                    className={`nb-item${!item.is_read ? " nb-item-unread" : ""}${hasBooking ? " nb-item-clickable" : ""}`}
                    onClick={() => goBooking(item.bookingid)}
                  >
                    <span className="nb-dot" style={{ background: meta.dot }} />
                    <div className="nb-item-body">
                      <div className="nb-item-name">{item.guestname}</div>
                      <div className="nb-item-meta">
                        {item.message
                          ? item.message.slice(0, 60) + (item.message.length > 60 ? "…" : "")
                          : item.roomnumber ? `Room ${item.roomnumber}` : ""}
                        {" · "}{timeAgo(item.created_at)}
                      </div>
                    </div>
                    <span className={`nb-tag ${meta.cls}`}>{meta.label}</span>
                  </div>
                );
              }
              const meta = BOOKING_META[item.type];
              const date = item.type === "departure" ? item.checkOut : item.checkIn;
              const roomDisplay = (item.allRooms?.length > 1)
                ? `Rooms ${item.roomNumbers}`
                : `Room ${item.roomNumber}`;
              return (
                <div
                  key={`${item.id}-${i}`}
                  className="nb-item nb-item-clickable"
                  onClick={() => goBooking(item.bookingId)}
                >
                  <span className="nb-dot" style={{ background: meta.dot }} />
                  <div className="nb-item-body">
                    <div className="nb-item-name">{item.guestName}</div>
                    <div className="nb-item-meta">
                      {roomDisplay} · {fmt(date)} · {item.nights}n
                    </div>
                  </div>
                  <span className={`nb-tag ${meta.cls}`}>{meta.label}</span>
                </div>
              );
            })}
          </div>

          <button className="nb-view-all" onClick={goFull}>
            View all notifications →
          </button>
        </div>
      )}
    </div>
  );
}
